import { createTheme } from "@mui/material/styles";
import { C, LAYOUT } from "../constants/colors";
import { FONTS } from "./tokens";

export function createAppTheme() {
  return createTheme({
    palette: {
      mode: "light",
      primary: { main: C.primary, dark: C.primaryDark, light: C.primary2, contrastText: "#fff" },
      error: { main: C.error },
      success: { main: C.success },
      warning: { main: C.warning },
      background: { default: C.bg, paper: C.surface },
      text: { primary: C.text, secondary: C.muted },
      divider: C.border,
    },
    shape: { borderRadius: LAYOUT.radius },
    typography: {
      fontFamily: FONTS.sans,
      h1: { fontWeight: 700, letterSpacing: FONTS.letterSpacingTight },
      h2: { fontWeight: 700, letterSpacing: FONTS.letterSpacingTight },
      h3: { fontWeight: 600, letterSpacing: FONTS.letterSpacingTight },
      h4: { fontWeight: 600, letterSpacing: FONTS.letterSpacingTight },
      h5: { fontWeight: 700, letterSpacing: "-0.015em" },
      h6: { fontWeight: 700, letterSpacing: FONTS.letterSpacing },
      button: { fontWeight: 600, textTransform: "none", letterSpacing: FONTS.letterSpacing },
      body1: { letterSpacing: FONTS.letterSpacing },
      body2: { letterSpacing: FONTS.letterSpacing },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: FONTS.sans,
            letterSpacing: FONTS.letterSpacing,
            backgroundColor: C.bg,
            color: C.text,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "::selection": {
            background: C.accentSoft,
            color: C.text,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: LAYOUT.radiusSm,
            boxShadow: "none",
            fontWeight: 600,
            paddingInline: 16,
          },
          containedPrimary: {
            background: C.primary,
            "&:hover": { background: C.primaryHover || C.primaryDark, boxShadow: "none" },
          },
          outlinedPrimary: {
            borderColor: C.borderStrong,
            color: C.primary,
            "&:hover": { borderColor: C.primary, background: C.primarySoft },
          },
        },
        defaultProps: { disableElevation: true },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderBottom: `1px solid ${C.border}`,
            backdropFilter: "blur(10px)",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderRight: "none" },
        },
      },
      MuiTextField: {
        defaultProps: { size: "small" },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: LAYOUT.radiusSm,
            background: C.surface,
            fontFamily: FONTS.sans,
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: C.borderStrong },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: C.primary, borderWidth: 1.5 },
          },
          input: {
            fontFamily: FONTS.sans,
            fontSize: 14,
            padding: "11px 12px",
          },
          notchedOutline: { borderColor: C.borderStrong },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 700, borderRadius: 999, fontFamily: FONTS.sans },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { color: C.borderStrong, "&.Mui-checked": { color: C.primary } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            background: C.ink,
            color: "#fff",
            borderColor: `rgba(${C.primaryRgb},0.45)`,
          },
          body: {
            borderColor: C.border,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: LAYOUT.radiusSm },
        },
      },
    },
  });
}

export const muiTheme = createAppTheme();
export default muiTheme;
